import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { motion, AnimatePresence } from "framer-motion";

// MUI Icons
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import GroupsIcon from "@mui/icons-material/Groups";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import CodeIcon from "@mui/icons-material/Code";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SchoolIcon from "@mui/icons-material/School";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import LoyaltyIcon from "@mui/icons-material/Loyalty";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import InventoryIcon from "@mui/icons-material/Inventory";
import HandshakeIcon from "@mui/icons-material/Handshake";
import StorageIcon from "@mui/icons-material/Storage";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import StarIcon from "@mui/icons-material/Star";

// Logos - Update these paths to match your actual asset locations
import figLogo from "../../assets/logos/fig_logo.png";
import swanSolutionsLogo from "../../assets/logos/swansolutions_logo.png";
import swanAiLabsLogo from "../../assets/logos/swan_ai_labs_logo.png";

const glassStyles = {
    card: {
        background: "rgba(255, 255, 255, 0.12)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        border: "1px solid rgba(255, 255, 255, 0.25)",
        boxShadow: "0 8px 32px rgba(0, 40, 80, 0.12), inset 0 1px 1px rgba(255,255,255,0.4)",
    },
    cardHover: {
        background: "rgba(255, 255, 255, 0.18)",
        border: "1px solid rgba(33, 219, 184, 0.4)",
        boxShadow: "0 12px 48px rgba(33, 219, 184, 0.15), inset 0 1px 2px rgba(255,255,255,0.5)",
    },
    chip: {
        background: "rgba(33, 219, 184, 0.15)",
        backdropFilter: "blur(8px)",
        border: "1px solid rgba(33, 219, 184, 0.3)",
    },
};

const cardHaptic = {
    whileHover: { scale: 1.015, y: -6 },
    whileTap: { scale: 0.985 },
    transition: { type: "spring", stiffness: 300, damping: 20 },
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.08, delayChildren: 0.1 },
    },
};

const staggerItem = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100, damping: 15 } },
};

const categories = [
    { id: "all", label: "All Solutions", icon: <StarIcon /> },
    { id: "nutrition", label: "Nutrition Suite", icon: <RestaurantMenuIcon /> },
    { id: "platform", label: "Platform & AI", icon: <SmartToyIcon /> },
    { id: "enterprise", label: "Enterprise", icon: <CodeIcon /> },
];

const solutions = [
    {
        id: "fig-professional-dev",
        category: "nutrition",
        title: "FIG Professional Development",
        subtitle: "Training & Compliance Platform",
        image: figLogo,
        badge: "FLAGSHIP",
        badgeColor: "#21dbb8",
        features: [
            { icon: <SchoolIcon />, label: "Online Training Modules", desc: "Self-paced learning paths" },
            { icon: <VerifiedUserIcon />, label: "Certification Tracking", desc: "Automated renewal alerts" },
            { icon: <AutoAwesomeIcon />, label: "Compliance Dashboards", desc: "Real-time status visibility" },
            { icon: <GroupsIcon />, label: "Team Management", desc: "Track entire department progress" },
        ],
        description:
            "Modernize staff training and compliance management. Deliver online modules, track certifications, manage corrective actions, and maintain audit-ready documentation with actionable dashboards.",
        highlight: "No more spreadsheets. Just proof for every review.",
        cta: "Explore Professional Dev",
        link: "/contact",
    },
    {
        id: "fig-finance",
        category: "nutrition",
        title: "FIG Finance",
        subtitle: "School Nutrition Financial Management",
        image: figLogo,
        badge: "USFA DIRECTORS CHOICE 2025",
        badgeColor: "#d4af37",
        features: [
            { icon: <VerifiedUserIcon />, label: "Audit-Ready Reporting", desc: "Always prepared for review" },
            { icon: <InventoryIcon />, label: "Procurement Tracking", desc: "Every dollar accounted for" },
            { icon: <HandshakeIcon />, label: "Vendor Management", desc: "Automated workflows" },
            { icon: <StorageIcon />, label: "Budget Forecasting", desc: "Predictive financial planning" },
        ],
        description:
            "The leading procurement and finance platform built specifically for K12 nutrition programs. Track every dollar, automate vendor management, and generate audit-ready reports instantly.",
        highlight: "Replace generic accounting with nutrition-specific financial tools.",
        cta: "Explore FIG Finance",
        link: "/contact",
    },
    {
        id: "fig-procurement-analysis",
        category: "nutrition",
        title: "FIG Procurement Analysis",
        subtitle: "AI-Powered Invoice Intelligence",
        image: figLogo,
        badge: "AI POWERED",
        badgeColor: "#9c27b0",
        features: [
            { icon: <SmartToyIcon />, label: "AI Invoice Review", desc: "Automated line-item analysis" },
            { icon: <StorageIcon />, label: "Complete Document Management", desc: "Centralized invoice repository" },
            { icon: <IntegrationInstructionsIcon />, label: "GDSN Integration", desc: "Real-time product data sync" },
            { icon: <VerifiedUserIcon />, label: "Bid Comparison Engine", desc: "Auto-compare against district bids" },
            { icon: <AutoAwesomeIcon />, label: "Variance Detection", desc: "Identify pricing discrepancies" },
            { icon: <InventoryIcon />, label: "POI & Credit Reporting", desc: "Automate vendor credit requests" },
        ],
        description:
            "Revolutionary AI-assisted invoice review with complete document management. Automatically compares every line item across all invoices against your district bid. Identifies and catalogs pricing variances instantly, generating reports to ensure proof of invoice accuracy and streamlined vendor credit requests.",
        highlight: "Every invoice analyzed. Every variance caught. Every dollar recovered.",
        cta: "Discover Procurement Analysis",
        link: "/contact",
    },
    {
        id: "fig-pos",
        category: "nutrition",
        title: "FIG Point of Sale",
        subtitle: "The Most Advanced K12 POS System",
        image: figLogo,
        badge: "LOYALTY PROGRAM",
        badgeColor: "#e91e63",
        features: [
            { icon: <TouchAppIcon />, label: "Palm Scanning Technology", desc: "Contactless biometric identification" },
            { icon: <PhoneIphoneIcon />, label: "Mobile Device Cashier", desc: "Flexible checkout anywhere" },
            { icon: <SchoolIcon />, label: "Student Mobile App", desc: "Pre-order, view menus, track nutrition" },
            { icon: <LoyaltyIcon />, label: "Loyalty Points Program", desc: "Reward healthy choices" },
        ],
        description:
            "Revolutionary point-of-sale built for K12 nutrition. Biometric palm scanning eliminates PINs and cards. Mobile cashier stations serve anywhere on campus. Students order ahead, parents manage accounts, and loyalty programs encourage healthy eating habits.",
        highlight: "Eliminate lunch lines. Increase participation. Reward healthy choices.",
        cta: "Explore FIG POS",
        link: "/contact",
    },
    {
        id: "fig-special-diets",
        category: "nutrition",
        title: "FIG Special Diets",
        subtitle: "Complete Dietary Management Platform",
        image: figLogo,
        badge: "HIPAA COMPLIANT",
        badgeColor: "#0090B3",
        features: [
            { icon: <PhoneIphoneIcon />, label: "Mobile Diet Order Capture", desc: "Parents submit requests anywhere" },
            { icon: <HealthAndSafetyIcon />, label: "HIPAA Compliant Communication", desc: "Secure health data exchange" },
            { icon: <AutoAwesomeIcon />, label: "AI-Assisted Form Completion", desc: "Smart auto-fill from documents" },
            { icon: <InventoryIcon />, label: "Complete Product Management", desc: "Track every ingredient" },
            { icon: <SmartToyIcon />, label: "AI-Assisted Recipe Development", desc: "Create compliant alternatives" },
        ],
        description:
            "End-to-end special diets management with HIPAA-compliant communication between families, nurses, and nutrition staff. AI assists with form completion from uploaded medical documents and develops allergen-free recipe alternatives automatically.",
        highlight: "Protect every student. Simplify compliance. Empower families.",
        cta: "Discover Special Diets",
        link: "/contact",
    },
    {
        id: "fig-coop",
        category: "nutrition",
        title: "FIG Buying Cooperative",
        subtitle: "Procurement Power Through Collaboration",
        image: figLogo,
        badge: "GDSN INTEGRATED",
        badgeColor: "#145c8c",
        features: [
            { icon: <GroupsIcon />, label: "Cooperative Purchasing", desc: "Aggregate volume for better pricing" },
            { icon: <StorageIcon />, label: "GDSN Integration", desc: "Global Data Synchronization Network" },
            { icon: <VerifiedUserIcon />, label: "USDA FoodData Central", desc: "Authoritative nutrition data" },
            { icon: <HandshakeIcon />, label: "Vendor Management", desc: "Streamlined bidding and contracts" },
        ],
        description:
            "Unite districts for collective purchasing power. Direct integration with GDSN and USDA FoodData Central ensures accurate product data, nutritional information, and compliance. Manage bids, contracts, and vendor relationships in one platform.",
        highlight: "Buy together. Save together. Succeed together.",
        cta: "Join the Cooperative",
        link: "/contact",
    },
    {
        id: "swan-ai-labs",
        category: "platform",
        title: "Swan AI Labs",
        subtitle: "Applied AI for K12 Operations",
        image: swanAiLabsLogo,
        badge: "AI POWERED",
        badgeColor: "#9c27b0",
        features: [
            { icon: <SmartToyIcon />, label: "Machine Learning", desc: "Predictive analytics" },
            { icon: <AutoAwesomeIcon />, label: "Computer Vision", desc: "Invoice & document processing" },
            { icon: <RocketLaunchIcon />, label: "Rapid Prototyping", desc: "Concept to deployment" },
        ],
        description:
            "Turn AI research into operational results. We prototype and deploy machine learning, computer vision, and predictive analytics for K12 and EdTech organizations. Automate invoice review, optimize menus, forecast costs, and surface hidden insights.",
        highlight: "Ready for next-generation EdTech? We build it.",
        cta: "Explore AI Labs",
        link: "/contact",
    },
    {
        id: "integrations",
        category: "enterprise",
        title: "Custom Integrations",
        subtitle: "Connect Everything Seamlessly",
        image: swanSolutionsLogo,
        badge: null,
        features: [
            { icon: <IntegrationInstructionsIcon />, label: "API Development", desc: "RESTful & GraphQL" },
            { icon: <StorageIcon />, label: "Data Pipelines", desc: "ETL & real-time sync" },
            { icon: <CodeIcon />, label: "System Connectors", desc: "SIS, POS, State systems" },
        ],
        description:
            "Make your systems work together. We build secure, scalable APIs and data pipelines connecting SIS, POS, state systems, procurement, HR, and every platform your district depends on.",
        highlight: "Unified reporting. Zero manual entry. Total automation.",
        cta: "See Integration Services",
        link: "/contact",
    },
    {
        id: "software-dev",
        category: "enterprise",
        title: "Software Development",
        subtitle: "Enterprise Solutions Built Right",
        image: swanSolutionsLogo,
        badge: null,
        features: [
            { icon: <CodeIcon />, label: "Full-Stack Development", desc: "Web, mobile, desktop" },
            { icon: <SchoolIcon />, label: "K12 Expertise", desc: "We know your workflows" },
            { icon: <RocketLaunchIcon />, label: "Rapid Delivery", desc: "Agile methodology" },
        ],
        description:
            "Custom software for K12 districts and vendors. Beyond nutrition, we design full-scale, enterprise-grade systems for finance, workflow, special diets, records, procurement, and vendor platforms.",
        highlight: "Your vision. Your budget. Our reliable technology.",
        cta: "Start Your Project",
        link: "/contact",
    },
];

function GlassCard({ solution }) {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            variants={staggerItem}
            {...cardHaptic}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            style={{ height: "100%" }}
        >
            <Box
                sx={{
                    height: "100%",
                    borderRadius: 4,
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    ...(isHovered ? glassStyles.cardHover : glassStyles.card),
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "3px",
                        background: isHovered
                            ? "linear-gradient(90deg, #21dbb8, #00CFFE, #145c8c)"
                            : "transparent",
                        transition: "all 0.3s ease",
                    },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
                    <Box
                        component={motion.div}
                        animate={{ rotate: isHovered ? [0, -5, 5, 0] : 0 }}
                        transition={{ duration: 0.4 }}
                        sx={{
                            width: 60,
                            height: 60,
                            borderRadius: 3,
                            background: "rgba(255,255,255,0.9)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 4px 16px rgba(0,40,80,0.1)",
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            component="img"
                            src={solution.image}
                            alt={solution.title}
                            onError={(e) => {
                                // Fallback if image fails to load
                                e.target.style.display = 'none';
                            }}
                            sx={{
                                width: 44,
                                height: 44,
                                objectFit: "contain",
                            }}
                        />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 800,
                                color: "#003366",
                                fontSize: "1.25rem",
                                lineHeight: 1.2,
                                mb: 0.5,
                            }}
                        >
                            {solution.title}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: "#0055A4", fontWeight: 500, fontSize: "0.85rem" }}
                        >
                            {solution.subtitle}
                        </Typography>
                    </Box>
                    {solution.badge && (
                        <Chip
                            label={solution.badge}
                            size="small"
                            sx={{
                                ...glassStyles.chip,
                                background: `${solution.badgeColor}20`,
                                borderColor: solution.badgeColor,
                                color: solution.badgeColor,
                                fontWeight: 700,
                                fontSize: "0.65rem",
                                letterSpacing: "0.05em",
                                height: 24,
                            }}
                        />
                    )}
                </Box>

                <Typography
                    variant="body2"
                    sx={{
                        color: "#003366",
                        mb: 2,
                        lineHeight: 1.6,
                        fontSize: "0.92rem",
                    }}
                >
                    {solution.description}
                </Typography>

                <Box
                    component={motion.div}
                    initial={false}
                    animate={{ height: isHovered ? "auto" : 0, opacity: isHovered ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                    sx={{ overflow: "hidden", mb: isHovered ? 2 : 0 }}
                >
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                            gap: 1.5,
                            pt: 1,
                        }}
                    >
                        {solution.features.map((feature, i) => (
                            <motion.div
                                key={feature.label}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.05 }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 1,
                                        p: 1,
                                        borderRadius: 2,
                                        background: "rgba(33, 219, 184, 0.08)",
                                        border: "1px solid rgba(33, 219, 184, 0.15)",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            color: "#21dbb8",
                                            mt: 0.25,
                                            "& svg": { fontSize: "1rem" },
                                        }}
                                    >
                                        {feature.icon}
                                    </Box>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: "0.75rem",
                                                color: "#003366",
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {feature.label}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: "0.68rem",
                                                color: "#0055A4",
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {feature.desc}
                                        </Typography>
                                    </Box>
                                </Box>
                            </motion.div>
                        ))}
                    </Box>
                </Box>

                <Box
                    sx={{
                        mt: "auto",
                        pt: 2,
                        borderTop: "1px solid rgba(0, 51, 102, 0.1)",
                    }}
                >
                    <Typography
                        sx={{
                            color: "#21dbb8",
                            fontWeight: 600,
                            fontSize: "0.88rem",
                            fontStyle: "italic",
                            mb: 2,
                        }}
                    >
                        "{solution.highlight}"
                    </Typography>

                    <Button
                        component={motion.a}
                        href={solution.link}
                        whileHover={{ scale: 1.03, x: 4 }}
                        whileTap={{ scale: 0.97 }}
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                            width: "100%",
                            py: 1.25,
                            borderRadius: 3,
                            fontWeight: 700,
                            fontSize: "0.95rem",
                            background: isHovered
                                ? "linear-gradient(135deg, #145c8c 0%, #21dbb8 100%)"
                                : "linear-gradient(135deg, #003366 0%, #0055A4 100%)",
                            color: "#fff",
                            boxShadow: isHovered
                                ? "0 6px 24px rgba(33, 219, 184, 0.3)"
                                : "0 4px 16px rgba(0, 51, 102, 0.2)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                                background: "linear-gradient(135deg, #145c8c 0%, #21dbb8 100%)",
                            },
                        }}
                    >
                        {solution.cta}
                    </Button>
                </Box>
            </Box>
        </motion.div>
    );
}

function AnimatedBackground() {
    return (
        <Box
            sx={{
                position: "absolute",
                inset: 0,
                overflow: "hidden",
                zIndex: 0,
            }}
        >
            {[
                { color: "rgba(33, 219, 184, 0.3)", size: 600, x: "10%", y: "-20%", duration: 25 },
                { color: "rgba(0, 207, 254, 0.25)", size: 500, x: "70%", y: "60%", duration: 30 },
                { color: "rgba(20, 92, 140, 0.2)", size: 450, x: "90%", y: "10%", duration: 20 },
                { color: "rgba(0, 51, 102, 0.15)", size: 550, x: "-10%", y: "80%", duration: 35 },
            ].map((orb, i) => (
                <motion.div
                    key={i}
                    animate={{
                        x: [0, 50, -30, 0],
                        y: [0, -40, 30, 0],
                        scale: [1, 1.1, 0.95, 1],
                    }}
                    transition={{
                        duration: orb.duration,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                    style={{
                        position: "absolute",
                        left: orb.x,
                        top: orb.y,
                        width: orb.size,
                        height: orb.size,
                        borderRadius: "50%",
                        background: `radial-gradient(circle, ${orb.color} 0%, transparent 70%)`,
                        filter: "blur(60px)",
                    }}
                />
            ))}

            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage: `
                        linear-gradient(rgba(0, 51, 102, 0.03) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(0, 51, 102, 0.03) 1px, transparent 1px)
                    `,
                    backgroundSize: "60px 60px",
                }}
            />
        </Box>
    );
}

export default function Solutions() {
    const [activeCategory, setActiveCategory] = useState("all");

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, []);

    const filteredSolutions =
        activeCategory === "all"
            ? solutions
            : solutions.filter((s) => s.category === activeCategory);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                background: "linear-gradient(180deg, #e8f4f8 0%, #f0f7fa 50%, #f7fbfc 100%)",
                position: "relative",
            }}
        >
            <AnimatedBackground />

            {/* HERO */}
            <Box
                component={motion.section}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
                sx={{
                    position: "relative",
                    width: "100vw",
                    left: "50%",
                    right: "50%",
                    marginLeft: "-50vw",
                    marginRight: "-50vw",
                    py: { xs: 10, md: 14 },
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                            "linear-gradient(135deg, #0b223e 0%, #145c8c 40%, #21dbb8 100%)",
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            background:
                                "radial-gradient(ellipse at 30% 20%, rgba(33, 219, 184, 0.4) 0%, transparent 50%)",
                        },
                    }}
                />

                {[...Array(20)].map((_, i) => (
                    <motion.div
                        key={i}
                        animate={{
                            y: [-20, 20],
                            x: [-10, 10],
                            opacity: [0.3, 0.7, 0.3],
                        }}
                        transition={{
                            duration: 3 + Math.random() * 4,
                            repeat: Infinity,
                            repeatType: "reverse",
                            delay: Math.random() * 2,
                        }}
                        style={{
                            position: "absolute",
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            width: 4 + Math.random() * 6,
                            height: 4 + Math.random() * 6,
                            borderRadius: "50%",
                            background: "rgba(255, 255, 255, 0.5)",
                        }}
                    />
                ))}

                <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                    <Box sx={{ textAlign: "center" }}>
                        <motion.div
                            initial={{ opacity: 0, y: -30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                        >
                            <Box
                                component="img"
                                src={swanSolutionsLogo}
                                alt="Swan Solutions"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                                sx={{
                                    height: { xs: 70, md: 110 },
                                    mb: 3,
                                    filter: "drop-shadow(0 8px 32px rgba(0,0,0,0.3))",
                                }}
                            />
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4, duration: 0.6 }}
                        >
                            <Chip
                                label="THE MOST ADVANCED K12 NUTRITION SOFTWARE SUITE"
                                sx={{
                                    mb: 3,
                                    py: 2.5,
                                    px: 3,
                                    height: "auto",
                                    background: "rgba(255, 255, 255, 0.15)",
                                    backdropFilter: "blur(12px)",
                                    border: "1px solid rgba(255, 255, 255, 0.3)",
                                    color: "#fff",
                                    fontWeight: 800,
                                    fontSize: { xs: "0.7rem", md: "0.8rem" },
                                    letterSpacing: "0.12em",
                                    "& .MuiChip-label": { px: 0 },
                                }}
                            />
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5, duration: 0.6 }}
                        >
                            <Typography
                                variant="h1"
                                sx={{
                                    fontWeight: 900,
                                    color: "#fff",
                                    fontSize: { xs: "2.5rem", md: "4rem" },
                                    lineHeight: 1.1,
                                    mb: 2,
                                    textShadow: "0 4px 24px rgba(0,0,0,0.2)",
                                }}
                            >
                                Our Solutions
                            </Typography>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6, duration: 0.6 }}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    color: "rgba(255, 255, 255, 0.9)",
                                    fontWeight: 400,
                                    maxWidth: 800,
                                    mx: "auto",
                                    fontSize: { xs: "1.1rem", md: "1.4rem" },
                                    lineHeight: 1.6,
                                }}
                            >
                                Enterprise software for K12 nutrition, operations, and EdTech partners.
                                Designed, built, and supported by people who know school business.
                            </Typography>
                        </motion.div>
                    </Box>
                </Container>
            </Box>

            {/* CATEGORY TABS */}
            <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                <Box
                    component={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 0.5 }}
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: -5,
                        mb: 6,
                    }}
                >
                    <Box
                        sx={{
                            ...glassStyles.card,
                            borderRadius: 4,
                            p: 1,
                        }}
                    >
                        <Tabs
                            value={activeCategory}
                            onChange={(_, v) => setActiveCategory(v)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                minHeight: 48,
                                "& .MuiTabs-indicator": {
                                    height: 3,
                                    borderRadius: 2,
                                    background: "linear-gradient(90deg, #21dbb8, #00CFFE)",
                                },
                            }}
                        >
                            {categories.map((cat) => (
                                <Tab
                                    key={cat.id}
                                    value={cat.id}
                                    label={
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            {cat.icon}
                                            <span>{cat.label}</span>
                                        </Box>
                                    }
                                    component={motion.button}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: "0.9rem",
                                        color: activeCategory === cat.id ? "#003366" : "#0055A4",
                                        minHeight: 48,
                                        px: 2.5,
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                            color: "#003366",
                                            background: "rgba(33, 219, 184, 0.08)",
                                        },
                                    }}
                                />
                            ))}
                        </Tabs>
                    </Box>
                </Box>

                {/* SOLUTIONS GRID */}
                <Box sx={{ pb: 10 }}>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeCategory}
                            variants={staggerContainer}
                            initial="hidden"
                            animate="visible"
                            exit="hidden"
                        >
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        md: "repeat(2, 1fr)",
                                        lg: "repeat(3, 1fr)",
                                    },
                                    gap: 4,
                                }}
                            >
                                {filteredSolutions.map((solution) => (
                                    <GlassCard key={solution.id} solution={solution} />
                                ))}
                            </Box>
                        </motion.div>
                    </AnimatePresence>
                </Box>
            </Container>

            {/* BOTTOM CTA */}
            <Box
                component={motion.section}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                sx={{
                    position: "relative",
                    py: { xs: 10, md: 14 },
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                            "linear-gradient(135deg, rgba(11, 34, 62, 0.03) 0%, rgba(33, 219, 184, 0.08) 100%)",
                    }}
                />

                <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
                    <Box
                        sx={{
                            ...glassStyles.card,
                            borderRadius: 5,
                            p: { xs: 4, md: 6 },
                            textAlign: "center",
                        }}
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: 900,
                                    color: "#003366",
                                    mb: 2,
                                    fontSize: { xs: "1.8rem", md: "2.4rem" },
                                }}
                            >
                                Your Vision. Our Technology.
                            </Typography>
                            <Typography
                                sx={{
                                    color: "#0055A4",
                                    fontSize: "1.15rem",
                                    maxWidth: 600,
                                    mx: "auto",
                                    mb: 4,
                                    lineHeight: 1.7,
                                }}
                            >
                                Swan Solutions is your partner for practical, reliable, and future-ready
                                software. Let's build something that transforms your district or business.
                            </Typography>

                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 2,
                                    justifyContent: "center",
                                    flexWrap: "wrap",
                                }}
                            >
                                <Button
                                    component={motion.a}
                                    href="/contact"
                                    whileHover={{ scale: 1.04, y: -2 }}
                                    whileTap={{ scale: 0.96 }}
                                    size="large"
                                    endIcon={<ArrowForwardIcon />}
                                    sx={{
                                        py: 1.5,
                                        px: 4,
                                        borderRadius: 99,
                                        fontWeight: 700,
                                        fontSize: "1.1rem",
                                        background: "linear-gradient(135deg, #145c8c 0%, #21dbb8 100%)",
                                        color: "#fff",
                                        boxShadow: "0 8px 32px rgba(33, 219, 184, 0.3)",
                                        "&:hover": {
                                            boxShadow: "0 12px 40px rgba(33, 219, 184, 0.4)",
                                        },
                                    }}
                                >
                                    Start the Conversation
                                </Button>
                                <Button
                                    component={motion.a}
                                    href="/labs"
                                    whileHover={{ scale: 1.04, y: -2 }}
                                    whileTap={{ scale: 0.96 }}
                                    size="large"
                                    variant="outlined"
                                    sx={{
                                        py: 1.5,
                                        px: 4,
                                        borderRadius: 99,
                                        fontWeight: 700,
                                        fontSize: "1.1rem",
                                        borderColor: "#003366",
                                        borderWidth: 2,
                                        color: "#003366",
                                        "&:hover": {
                                            borderWidth: 2,
                                            background: "rgba(0, 51, 102, 0.05)",
                                        },
                                    }}
                                >
                                    Explore AI Labs
                                </Button>
                            </Box>
                        </motion.div>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}